<template>
  <div v-if="track && Object.keys(track).length" :class="[$style.track, 'container']">
    <AudioHeader :audio-data="track" :class="$style.header"></AudioHeader>
    <ProcessContent wrapper="section" :content="track.content"></ProcessContent>
    <AudioTags v-if="track.tags.nodes.length" :tags="track.tags.nodes"></AudioTags>
  </div>
</template>

<script setup lang="ts">
import { AudioData } from '@/types/audio.interface';
import AudioHeader from '@/components/audio/AudioHeader.vue';
import { audioTrackQuery } from '@/queries/GetAudioTrack';
import { defineAsyncComponent, ref } from 'vue';
import { dataLoaded, useHandleError, useMetaData } from '@/composables/Common.js';
import ProcessContent from '@/components/common/ProcessContent.vue';
import { useQuery } from 'villus';

const AudioTags = defineAsyncComponent(() => import('@/components/audio/AudioTags.vue'));

const props = defineProps<{
  slug: string;
}>();

const { handleError } = useHandleError();
const { setMetaDescription, setMetaTitle } = useMetaData();

const track = ref<AudioData>();

const { data } = useQuery({
  onData: data => {
    if (data.audioTrack && Object.keys(data.audioTrack).length) {
      track.value = data.audioTrack;
      setMetaTitle(data.audioTrack.title);
      setMetaDescription(data.audioTrack.excerpt);
      dataLoaded();
    }

    if (!data.audioTrack || Object.keys(data.audioTrack).length === 0) {
      handleError('AudioTrack', props.slug);
    }
  },
  query: audioTrackQuery,
  variables: { slug: props.slug },
});
</script>

<script lang="ts">
export default {
  beforeRoute(params, villusClient) {
    return new Promise<void>((resolve, reject) => {
      villusClient
        .executeQuery({
          query: audioTrackQuery,
          variables: {
            slug: params.slug,
          },
        })
        .then(() => resolve())
        .catch((err) => reject(err));
    });
  },
};
</script>

<style lang="scss" module>
@media (min-width: $break-point) {
  .track {
    @include grid;

    > * {
      grid-column: 1 / span 8;
    }

    .discover-more {
      grid-column: 1 / span 8;
    }

    .header {
      grid-column: span 12;
    }
  }
}
</style>
